import { useRef, useState } from "react";
import { MapPin, Phone, Mail, Building2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import PageHero from "@/components/site/PageHero";
import Captcha from "@/components/site/Captcha";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/lib/settings";
import { toast } from "sonner";
import { useSEO } from "@/lib/seo";

export default function Contact() {
  useSEO({
    title: "Contact Us",
    description: "Get in touch with PharmacoHires — for careers, partnerships or general enquiries.",
    pageKey: "contact",
  });
  const { settings } = useSiteSettings();
  const officeLine = (settings.offices || []).join(" · ");

  return (
    <>
      <PageHero
        overline="Contact"
        title="Let's talk."
        description="Have a question, a partnership idea or a career query? Drop us a line and we'll respond within one business day."
      />
      <section className="section-pad">
        <div className="container-page grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <h2 className="font-heading text-2xl font-semibold text-slate-900">Reach us</h2>
            <div className="mt-6 space-y-5 text-sm">
              <div className="flex items-start gap-3">
                <Mail className="mt-0.5 h-5 w-5 text-primary" />
                <div>
                  <div className="font-semibold text-slate-900">Email</div>
                  <a href={`mailto:${settings.contact_email}`} className="text-slate-600 hover:text-primary" data-testid="contact-info-email">{settings.contact_email}</a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="mt-0.5 h-5 w-5 text-primary" />
                <div>
                  <div className="font-semibold text-slate-900">Phone</div>
                  <div className="text-slate-600" data-testid="contact-info-phone">{settings.contact_phone}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-5 w-5 text-primary" />
                <div>
                  <div className="font-semibold text-slate-900">Offices</div>
                  <div className="text-slate-600" data-testid="contact-info-offices">{officeLine}</div>
                </div>
              </div>
            </div>
            <div className="mt-8 rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-700">
              <Building2 className="h-5 w-5 text-secondary" />
              <div className="mt-2 font-semibold">Business hours</div>
              <div className="mt-1 text-slate-600">Mon - Sat · 10:00 AM to 7:00 PM IST</div>
            </div>
            <div className="mt-6 rounded-xl border border-primary/30 bg-primary/5 p-5 text-sm">
              <div className="font-semibold text-primary">Looking to hire pharma talent?</div>
              <p className="mt-1 text-slate-700">Use our dedicated employer form for a faster, structured shortlist.</p>
              <a href="/hire-talent" className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline" data-testid="contact-hire-cta">
                Go to Hire Talent form →
              </a>
            </div>
          </div>

          <div className="md:col-span-7">
            <ContactForm />
          </div>
        </div>
      </section>
    </>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const captchaRef = useRef(null);
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { captcha_token, captcha_answer } = captchaRef.current?.getValues?.() || {};
      await api.post("/enquiries", { type: "contact", ...form, captcha_token, captcha_answer });
      toast.success("Message sent. We'll respond within one business day.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      captchaRef.current?.reset?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
      captchaRef.current?.reset?.();
    } finally {
      setSubmitting(false);
    }
  };
  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="contact-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div><Label htmlFor="c-name">Name</Label><Input id="c-name" required value={form.name} onChange={onChange("name")} data-testid="contact-name" /></div>
        <div><Label htmlFor="c-email">Email</Label><Input id="c-email" type="email" required value={form.email} onChange={onChange("email")} data-testid="contact-email" /></div>
        <div><Label htmlFor="c-phone">Phone</Label><Input id="c-phone" value={form.phone} onChange={onChange("phone")} data-testid="contact-phone" /></div>
        <div><Label htmlFor="c-subj">Subject</Label><Input id="c-subj" value={form.subject} onChange={onChange("subject")} data-testid="contact-subject" /></div>
      </div>
      <div><Label htmlFor="c-msg">Message</Label><Textarea id="c-msg" rows={5} required value={form.message} onChange={onChange("message")} data-testid="contact-message" /></div>
      <Captcha ref={captchaRef} testId="contact-captcha" />
      <Button type="submit" disabled={submitting} className="bg-primary hover:bg-primary/90" data-testid="contact-submit">
        {submitting ? "Sending…" : "Send message"}
      </Button>
    </form>
  );
}
